import type { Question, Option } from '../editor/types';
import { MAX_ANSWER_IMAGES, MAX_IMAGE_BYTES, textAnswerImages, textAnswerText, withTextAnswer, selectLimitOf, TEXT_VALIDATIONS, textValidationOf, matrixTextRowValidationOf, matrixColTextsOf, matrixTextColumnsOf, matrixTextCellValue, matrixTextCellValidationOf, matrixSliderImagesOf } from '../editor/types';
import ImageLightbox from '../ImageLightbox';
import DatePickerInput from './DatePickerInput';
import ScoreInput from './ScoreInput';
import { titleText } from '../editor/types';
import { useRef, useState } from 'react';

type FullOpt = { value: string; limit: number; used: number; onQuotaFull: 'jumpNext' | 'end' };

type Props = {
  question: Question;
  value: any;
  onChange: (v: any) => void;
  fullOptions?: FullOpt[];
  /** 0902 选项附属填空：本题 { 选项值: 已填文本 }（answers._optionText[题号]） */
  optionText?: Record<string, string>;
  onOptionTextChange?: (optionValue: string, text: string) => void;
  /** 1627 矩阵列附属填空：本题 { 行标题: 已填文本 }（answers._matrixColText[题号]） */
  matrixColText?: Record<string, string>;
  onMatrixColTextChange?: (row: string, text: string) => void;
  /** 0907 选项关联：本题当前允许展示的选项值集合（null / 缺省 = 不限制，展示全部） */
  allowedOptions?: Set<string> | null;
};

function quotaOf(opts: FullOpt[] | undefined, value: string): FullOpt | undefined {
  if (!opts) return undefined;
  return opts.find((o) => o.value === value);
}

function normalizeOpt(raw: string | Option): Option {
  return typeof raw === 'string' ? { value: raw } : raw;
}

/** 0907 选项关联：按允许集合过滤后的选项列表（未配置关联时原样返回全部） */
function visibleOptions(q: Question, allowed: Set<string> | null | undefined): Option[] {
  const all = (q.options ?? []).map(normalizeOpt);
  return allowed ? all.filter((o) => allowed.has(o.value)) : all;
}

type TextQuestionInputProps = {
  value: unknown;
  onChange: (value: unknown) => void;
  allowImageUpload: boolean;
  maxLength?: number;
  multiline?: boolean;
  /** 0903 填空校验：手机号用数字键盘；占位符提示格式 */
  inputMode?: 'numeric' | 'text';
  placeholder?: string;
  /** 0907 日期校验：输入框换成日历选择器 */
  dateMode?: boolean;
};

/** 文本/多行文本输入：开启答案配图时答案升级为 { text, images } 对象（最多 3 张，单张 ≤1.5MB） */
function TextQuestionInput({ value, onChange, allowImageUpload, maxLength, multiline = false, inputMode, placeholder, dateMode = false }: TextQuestionInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);
  const text = textAnswerText(value);
  const images = textAnswerImages(value);
  const hasStructuredAnswer = images.length > 0 || (value !== null && typeof value === 'object' && !Array.isArray(value));
  const setText = (nextText: string) => {
    onChange(allowImageUpload || hasStructuredAnswer ? withTextAnswer(nextText, images) : nextText);
  };
  const setImages = (nextImages: string[]) => onChange(withTextAnswer(text, nextImages));
  const handleFile = (file: File) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(file.type)) {
      alert('请选择 PNG、JPEG、GIF 或 WebP 图片');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      alert('图片不能超过 1.5MB');
      return;
    }
    if (images.length >= MAX_ANSWER_IMAGES) {
      alert('最多上传 3 张图片');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') setImages([...images, reader.result]);
    };
    reader.readAsDataURL(file);
  };
  const field = dateMode ? (
    <DatePickerInput value={text} onChange={setText} placeholder={placeholder ?? '请选择日期'} />
  ) : multiline ? (
    <textarea
      className="textarea"
      rows={3}
      maxLength={maxLength}
      value={text}
      onChange={(e) => setText(e.target.value)}
      placeholder="请输入"
    />
  ) : (
    <input
      className="input"
      value={text}
      onChange={(e) => setText(e.target.value)}
      placeholder={placeholder ?? '请输入'}
      maxLength={maxLength}
      inputMode={inputMode}
    />
  );
  if (!allowImageUpload) return field;
  return (
    <div className="text-answer-with-images">
      {field}
      <div className="answer-image-actions">
        <button
          type="button"
          className="answer-image-add"
          onClick={() => inputRef.current?.click()}
          disabled={images.length >= MAX_ANSWER_IMAGES}
        >
          <span aria-hidden="true">▧</span> 添加图片 ({images.length}/{MAX_ANSWER_IMAGES})
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
            e.target.value = '';
          }}
        />
      </div>
      {images.length > 0 && (
        <div className="answer-image-list">
          {images.map((image, index) => (
            <div className="answer-image-item" key={`${image.slice(0, 24)}-${index}`}>
              <button
                type="button"
                className="answer-image-preview"
                onClick={() => setLightbox({ src: image, alt: `回答图片 ${index + 1}` })}
                aria-label={`查看回答图片 ${index + 1}`}
              >
                <img src={image} alt={`回答图片 ${index + 1}`} />
              </button>
              <button
                type="button"
                className="answer-image-remove"
                onClick={() => setImages(images.filter((_, imageIndex) => imageIndex !== index))}
                aria-label={`删除回答图片 ${index + 1}`}
              >
                删除
              </button>
            </div>
          ))}
        </div>
      )}
      {lightbox && <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />}
    </div>
  );
}

/** 0902 选项附属填空：选项被选中后出现在该选项下方的文本框 */
function OptionTextField({ value, onChange, style }: { value: string; onChange: (text: string) => void; style?: React.CSSProperties }) {
  return (
    <input
      className="input rq-opt-text"
      value={value}
      placeholder="请填写"
      maxLength={2000}
      style={style}
      aria-label="选项补充说明"
      onChange={(e) => onChange(e.target.value)}
      onClick={(e) => e.stopPropagation()}
    />
  );
}

export default function QuestionInput({ question, value, onChange, fullOptions, optionText, onOptionTextChange, matrixColText, onMatrixColTextChange, allowedOptions }: Props) {
  const q = question;
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);

  switch (q.type) {
    case 'text': {
      // 0903 填空格式校验：按种类给输入框加长度上限 / 数字键盘 / 格式占位提示（拦截逻辑在 Runner 与服务端）
      // 0907 日期校验：输入框换成日历选择器
      const kind = textValidationOf(q);
      const rule = kind ? TEXT_VALIDATIONS[kind] : null;
      return (
        <TextQuestionInput
          value={value}
          allowImageUpload={Boolean((q.params as { allowImageUpload?: boolean } | undefined)?.allowImageUpload)}
          maxLength={rule?.maxLength}
          inputMode={rule?.inputMode}
          placeholder={rule?.hint}
          dateMode={kind === 'date'}
          onChange={onChange}
        />
      );
    }

    case 'longtext': {
      // 0904 起开放题也支持文本格式校验（姓名/手机号/身份证号）；与 text 共用 rule
      const maxLen = (q.params as any)?.maxLen;
      const kind = textValidationOf(q);
      const rule = kind ? TEXT_VALIDATIONS[kind] : null;
      return (
        <TextQuestionInput
          value={value}
          maxLength={rule?.maxLength ?? maxLen}
          multiline
          inputMode={rule?.inputMode}
          placeholder={rule?.hint}
          dateMode={kind === 'date'}
          allowImageUpload={Boolean((q.params as { allowImageUpload?: boolean } | undefined)?.allowImageUpload)}
          onChange={onChange}
        />
      );
    }

    case 'single':
      return (
        <div className="radio-group">
          {visibleOptions(q, allowedOptions).map((opt, i) => {
            const f = quotaOf(fullOptions, opt.value);
            // 本地选中：把"我刚选的"也算到 used 里，UI 立即递减剩余数
            const isSelectedByMe = value === opt.value;
            const localUsed = f ? f.used + (isSelectedByMe ? 1 : 0) : 0;
            const localFull = !!f && localUsed >= f.limit;
            // 剩余 = 总额 - 已被选的（含本端刚选的，max 后保底 0）；只要配了配额就始终显示「剩余可选：x」
            const remaining = f ? Math.max(0, f.limit - localUsed) : 0;
            return (
              <label
                key={i}
                className={`radio-item ${localFull ? 'is-disabled' : ''} ${opt.image ? 'has-image' : ''} ${opt.allowText && value === opt.value ? 'has-text' : ''}`}
                title={localFull ? '本选项名额已满' : f ? `剩余可选：${remaining}` : ''}
              >
                <div className="rq-meta">
                  <input
                    type="radio"
                    name={q.id}
                    checked={value === opt.value}
                    disabled={localFull}
                    onChange={() => onChange(opt.value)}
                  />
                  <span>{opt.value}</span>
                  {f && (
                    <span className={`quota-count${localFull ? ' is-full' : ''}`}>
                      {`剩余可选：${remaining}`}
                    </span>
                  )}
                </div>
                {opt.image && (
                  <button
                    type="button"
                    className="rq-opt-img-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setLightbox({ src: opt.image!, alt: opt.value });
                    }}
                    title="点击查看大图"
                    aria-label="点击查看大图"
                  >
                    <img src={opt.image} alt={opt.value} className="rq-opt-img" />
                  </button>
                )}
                {opt.allowText && value === opt.value && (
                  <OptionTextField
                    value={optionText?.[opt.value] ?? ''}
                    onChange={(t) => onOptionTextChange?.(opt.value, t)}
                  />
                )}
              </label>
            );
          })}
          {lightbox && <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />}
        </div>
      );

    case 'multi': {
      const arr = Array.isArray(value) ? value : [];
      // 0902 选数限制：勾选达到 maxSelect 后，其余未勾选项变为不可勾选
      const limit = selectLimitOf(q);
      const atMax = limit?.max != null && arr.length >= limit.max;
      // 0904 多选互斥：勾选互斥项 → 自动取消所有普通项；勾选普通项 → 自动取消所有互斥项（支持多组）
      const allOpts = (q.options ?? []).map(normalizeOpt);
      const exclusiveValues = new Set(allOpts.filter((o) => o.exclusive === true).map((o) => o.value));
      const toggleExclusive = (cur: string[], optValue: string, checked: boolean): string[] => {
        if (!checked) {
          // 取消勾选：正常 toggle
          return cur.filter((x) => x !== optValue);
        }
        if (exclusiveValues.has(optValue)) {
          // 勾互斥 → 只保留已有互斥项 + 这一项，普通项全清
          return [...cur.filter((v) => exclusiveValues.has(v) && v !== optValue), optValue];
        }
        // 勾普通 → 取消所有互斥项 + 保留已有普通项 + 这一项
        return [...cur.filter((v) => !exclusiveValues.has(v) && v !== optValue), optValue];
      };
      return (
        <div className="checkbox-group">
          {visibleOptions(q, allowedOptions).map((opt, i) => {
            const f = quotaOf(fullOptions, opt.value);
            const checked = arr.includes(opt.value);
            // 本地选中：把"我刚勾的"也算到 used 里，UI 立即递减剩余数
            const localUsed = f ? f.used + (checked ? 1 : 0) : 0;
            const localFull = !!f && localUsed >= f.limit;
            const remaining = f ? Math.max(0, f.limit - localUsed) : 0;
            const lockedByMax = atMax && !checked;
            // 0904 互斥项被任一互斥项勾选锁定后：本身已选不锁；勾了一个普通项后，其它互斥项不可勾（普通与互斥不能共存）
            const lockedByExclusive = !checked && exclusiveValues.size > 0
              ? (exclusiveValues.has(opt.value)
                ? arr.some((v) => !exclusiveValues.has(v)) // 已选普通 → 锁其它互斥
                : arr.some((v) => exclusiveValues.has(v))  // 已选互斥 → 锁其它普通
              )
              : false;
            return (
              <label
                key={i}
                className={`checkbox-item ${localFull ? 'is-disabled' : ''} ${lockedByMax || lockedByExclusive ? 'is-locked' : ''} ${opt.image ? 'has-image' : ''} ${opt.allowText && checked ? 'has-text' : ''}`}
                title={localFull ? '本选项名额已满' : f ? `剩余可选：${remaining}` : lockedByMax ? limit?.maxHint ?? '' : lockedByExclusive ? '与已选选项互斥' : ''}
              >
                <div className="rq-meta">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={localFull || lockedByMax || lockedByExclusive}
                    onChange={(e) => {
                      if (localFull || lockedByMax || lockedByExclusive) return;
                      const next = toggleExclusive(arr, opt.value, e.target.checked);
                      onChange(next);
                    }}
                  />
                  <span>{opt.value}</span>
                  {opt.exclusive && (
                    <span className="exclusive-tag" title="互斥项：勾选后会自动取消所有普通选项">互斥</span>
                  )}
                  {f && (
                    <span className={`quota-count${localFull ? ' is-full' : ''}`}>
                      {`剩余可选：${remaining}`}
                    </span>
                  )}
                </div>
                {opt.image && (
                  <button
                    type="button"
                    className="rq-opt-img-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setLightbox({ src: opt.image!, alt: opt.value });
                    }}
                    title="点击查看大图"
                    aria-label="点击查看大图"
                  >
                    <img src={opt.image} alt={opt.value} className="rq-opt-img" />
                  </button>
                )}
                {opt.allowText && checked && (
                  <OptionTextField
                    value={optionText?.[opt.value] ?? ''}
                    onChange={(t) => onOptionTextChange?.(opt.value, t)}
                  />
                )}
              </label>
            );
          })}
          {limit && (
            <div className="select-limit-hint">
              {[limit.minHint, limit.maxHint].filter(Boolean).join(' · ')}（已选 {arr.length} 项）
            </div>
          )}
          {lightbox && <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />}
        </div>
      );
    }

    case 'ranking':
      return <RankingInput question={q} value={value} onChange={onChange} allowedOptions={allowedOptions} />;

    case 'matrixslider': {
      // 0907 矩阵滑动题：每行左侧配图（可选）+ 行标题，右侧独立滑动条；拖动实时显示数值；答案 { 行: 数值 }
      const rows: string[] = q.params?.rows ?? [];
      const images = matrixSliderImagesOf(q);
      const p: any = q.params ?? {};
      const min = typeof p.min === 'number' ? p.min : 0;
      const max = typeof p.max === 'number' ? p.max : 100;
      const step = typeof p.step === 'number' && p.step > 0 ? p.step : 1;
      const map: Record<string, any> = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
      const setRow = (r: string, n: number | undefined) => {
        const next = { ...map };
        if (n === undefined) delete next[r];
        else next[r] = n;
        onChange(next);
      };
      return (
        <div className="matrixslider">
          {rows.map((r, i) => {
            const cur = typeof map[r] === 'number' ? (map[r] as number) : undefined;
            const img = images[i];
            return (
              <div key={r} className="matrixslider-row">
                {img && (
                  <button type="button" className="matrixslider-img-btn" onClick={() => setLightbox({ src: img, alt: r })} title="点击查看大图" aria-label="点击查看大图">
                    <img src={img} alt={r} className="matrixslider-img" />
                  </button>
                )}
                <div className="matrixslider-main">
                  <div className="matrixslider-label">{r}</div>
                  <ScoreInput {...p} min={min} max={max} step={step} value={cur} onChange={n => setRow(r, n)} label={`${titleText(q.title)}，${r}`} />
                </div>
              </div>
            );
          })}
          {lightbox && <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />}
        </div>
      );
    }

    case 'slider': {
      const p = (q.params ?? { min: 0, max: 100, step: 1 }) as {
        min: number;
        max: number;
        step?: number;
        unit?: string;
        minLabel?: string;
        maxLabel?: string;
        allowDecimal?: boolean;
      };
      return <ScoreInput {...p} value={typeof value === 'number' ? value : undefined} onChange={onChange} label={titleText(q.title)} />;
    }

    case 'select': {
      const shownOpts = visibleOptions(q, allowedOptions);
      const selectedOpt = shownOpts.find((o) => o.value === value);
      return (
        <div className="select-with-text">
          <div className="select" style={{ maxWidth: 360 }}>
            <select value={selectedOpt ? value : ''} onChange={(e) => onChange(e.target.value || undefined)}>
              <option value="">请选择</option>
              {shownOpts.map((opt, i) => {
                const f = quotaOf(fullOptions, opt.value);
                // 本地选中：把当前用户已选也算到 used 里，UI 立即递减剩余数
                const isSelectedByMe = value === opt.value;
                const localUsed = f ? f.used + (isSelectedByMe ? 1 : 0) : 0;
                const localFull = !!f && localUsed >= f.limit;
                // 剩余 = 总额 - 已被选的（max 后保底 0）；始终以「剩余可选：x」展示
                const remaining = f ? Math.max(0, f.limit - localUsed) : 0;
                const suffix = f ? `（剩余可选：${remaining}）` : '';
                return (
                  <option key={i} value={opt.value} disabled={localFull}>
                    {opt.value}{suffix}
                  </option>
                );
              })}
            </select>
          </div>
          {selectedOpt?.allowText && (
            <OptionTextField
              value={optionText?.[selectedOpt.value] ?? ''}
              onChange={(t) => onOptionTextChange?.(selectedOpt.value, t)}
              style={{ maxWidth: 360 }}
            />
          )}
        </div>
      );
    }

    case 'matrixtext': {
      // 0907 按行独立控件：每行可单独选 text/select；select 行从 rowControls[r].options 取选项
      // 答案统一 { 行: 文本 }（多列老数据不再支持）
      const rows: string[] = q.params?.rows ?? [];
      const rowControls: Record<string, { kind?: 'text' | 'select'; options?: string[] }> = ((q.params as any)?.rowControls as any) ?? {};
      const map: Record<string, any> = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
      return (
        <div className="matrixtext-wrap">
          <table className="matrixtext-table">
            <tbody>
              {rows.map((r) => {
                const ctrl = rowControls[r] ?? { kind: 'text' };
                const kind = matrixTextRowValidationOf(q, r);
                const rule = kind ? TEXT_VALIDATIONS[kind] : null;
                const cell = map[r] === undefined || map[r] === null ? '' : String(map[r]);
                if (ctrl.kind === 'select') {
                  const opts: string[] = Array.isArray(ctrl.options) ? ctrl.options : [];
                  return (
                    <tr key={r}>
                      <th scope="row" className="matrixtext-row-label">{r}</th>
                      <td>
                        <div className="matrixtext-cell-select">
                          <select value={cell} onChange={(e) => onChange({ ...map, [r]: e.target.value })} aria-label={r}>
                            <option value="">请选择</option>
                            {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                          </select>
                        </div>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={r}>
                    <th scope="row" className="matrixtext-row-label">{r}</th>
                    <td>
                      {kind === 'date' ? (
                        <DatePickerInput value={cell} onChange={(v) => onChange({ ...map, [r]: v })} ariaLabel={r} />
                      ) : (
                        <input
                          className="input"
                          value={cell}
                          placeholder={rule?.hint ?? '请输入'}
                          maxLength={rule?.maxLength}
                          inputMode={rule?.inputMode}
                          onChange={(e) => onChange({ ...map, [r]: e.target.value })}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    case 'matrix': {
      const rows = q.params?.rows ?? [];
      const cols = q.params?.columns ?? [];
      // 1627 矩阵列填空：与 columns 等长的 boolean 数组
      const colAllowText: boolean[] = Array.isArray((q.params as any)?.columnAllowText) ? ((q.params as any).columnAllowText as boolean[]) : [];
      const map = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
      return (
        <div className="matrix-wrap" style={{ overflowX: 'auto' }}>
          <table className="matrix-table" style={{ borderCollapse: 'collapse', minWidth: 360, tableLayout: 'auto' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '6px 10px' }}></th>
                {cols.map((c) => (
                  <th key={c} style={{ padding: '6px 10px', fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap' }}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const selectedCol = map[r];
                return (
                  <tr key={r} style={{ borderTop: '1px solid var(--border, #e5e7eb)' }}>
                    <td className="matrix-row-label" style={{ padding: '8px 10px', fontSize: 14 }}>{r}</td>
                    {cols.map((c) => (
                      <td key={c} style={{ textAlign: 'center', padding: '8px 10px', whiteSpace: 'nowrap' }}>
                        <input
                          type="radio"
                          name={`${q.id}__${r}`}
                          checked={selectedCol === c}
                          onChange={() => onChange({ ...map, [r]: c })}
                        />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* 1627 矩阵列填空：每个 allowText 列下方在「该行选中该列」时展示一个文本框 */}
          {cols.some((_, i) => colAllowText[i]) && (
            <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
              {rows.map((r) => {
                const selectedCol = map[r];
                const idx = selectedCol ? cols.indexOf(selectedCol) : -1;
                if (idx < 0 || !colAllowText[idx]) return null;
                return (
                  <div key={r} style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 1fr) 2fr', gap: 10, alignItems: 'center' }}>
                    <span style={{ fontSize: 13, color: 'var(--text-2, #68758a)' }}>{r} · {selectedCol}</span>
                    <input
                      className="input"
                      style={{ fontSize: 13 }}
                      placeholder="请补充说明"
                      maxLength={2000}
                      value={matrixColText?.[r] ?? ''}
                      onChange={(e) => onMatrixColTextChange?.(r, e.target.value)}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    case 'file':
      return <FileUpload value={value} onChange={onChange} />;

    default:
      return <div>未知题型</div>;
  }
}

/** 上传附件题：支持多张图片（≤4MB 自动压缩）或 PDF（≤3MB）。最多 6 份。
 *  答案形态：数组 [{ name, dataUrl }]（待提交）或 [{ attachment:true, name, type, size }]（落盘后回读）。
 *  兼容老单对象形态（自动归一化为单元素数组）。 */
function FileUpload({ value, onChange }: { value: any; onChange: (v: any) => void }) {
  // 归一化为数组
  const items: any[] = Array.isArray(value)
    ? value
    : value && typeof value === 'object'
      ? [value]
      : [];
  const inputRef = useRef<HTMLInputElement>(null);
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);
  const MAX_FILES = 6;
  const MAX_BYTES = 4 * 1024 * 1024;

  const handleFile = (file: File) => {
    if (!file) return;
    if (items.length >= MAX_FILES) {
      alert(`最多上传 ${MAX_FILES} 份附件`);
      return;
    }
    const isImage = file.type.startsWith('image/');
    if (!isImage && file.size > 3 * 1024 * 1024) {
      alert('PDF 不能超过 3MB');
      return;
    }
    if (isImage && file.size > 10 * 1024 * 1024) {
      alert('图片不能超过 10MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      if (!isImage || file.size <= 300 * 1024) {
        onChange([...items, { name: file.name, dataUrl }]);
        return;
      }
      // 大图等比缩到 1280px 内转 JPEG，控制请求体体积
      const img = new Image();
      img.onload = () => {
        const max = 1280;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        const ctx = canvas.getContext('2d')!;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const name = file.name.replace(/\.\w+$/, '') + '.jpg';
        onChange([...items, { name, dataUrl: canvas.toDataURL('image/jpeg', 0.85) }]);
      };
      img.onerror = () => alert('图片读取失败，请换一张试试');
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  };

  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    const room = MAX_FILES - items.length;
    if (room <= 0) {
      alert(`最多上传 ${MAX_FILES} 份附件`);
      return;
    }
    const picked = Array.from(files).slice(0, room);
    if (picked.length < files.length) alert(`最多上传 ${MAX_FILES} 份附件，本次只添加前 ${picked.length} 份`);
    for (const f of picked) handleFile(f);
  };

  const removeAt = (i: number) => {
    const next = items.filter((_, idx) => idx !== i);
    onChange(next.length ? next : undefined);
  };

  const renderItem = (item: any, i: number) => {
    const isImage = typeof item.type === 'string' && item.type.startsWith('image/');
    // 落盘后回读的旧 attachment 单对象：item.attachment === true，无 dataUrl，靠后端代理展示
    const src = item.dataUrl ?? (item.attachment === true && item.type ? undefined : undefined);
    const thumbSrc = item.dataUrl && isImage ? item.dataUrl : null;
    return (
      <div className="file-item" key={i}>
        {thumbSrc ? (
          <button
            type="button"
            className="file-thumb-btn"
            onClick={() => setLightbox({ src: thumbSrc, alt: item.name ?? `附件 ${i + 1}` })}
            aria-label="查看图片"
            title="点击查看大图"
          >
            <img src={thumbSrc} alt={item.name ?? `附件 ${i + 1}`} className="file-thumb" />
          </button>
        ) : (
          <div className="file-thumb-fallback" title={item.name}>
            {isImage ? 'IMG' : 'PDF'}
          </div>
        )}
        <span className="file-name" title={item.name ?? '附件'}>{item.name ?? '附件'}</span>
        <button type="button" className="opt-del" onClick={() => removeAt(i)} title="移除附件">✕</button>
      </div>
    );
  };

  return (
    <div className="file-upload" style={{ display: 'grid', gap: 8 }}>
      {items.length > 0 && (
        <div className="file-list" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {items.map((item, i) => renderItem(item, i))}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button type="button" className="btn" onClick={() => inputRef.current?.click()} disabled={items.length >= MAX_FILES}>
          {items.length > 0 ? `继续添加（${items.length}/${MAX_FILES}）` : '选择附件（图片 / PDF，可多选）'}
        </button>
        <span className="hint">单张图片 ≤4MB（自动压缩），PDF ≤3MB；最多 {MAX_FILES} 份</span>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,application/pdf"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
      />
      {lightbox && <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />}
    </div>
  );
}
/**
 * 0903 排序题（不做拖拽）：按点击顺序固定名次，先点击的永远是第 1 位，最后点击的为最大序号；
 * 点击已选中的选项 → 取消选择。答案是按名次排列的选项值数组；至少 / 最多可选几项（params.minSelect / maxSelect）显示在题目下方的副标题位置。
 * 预览面板与填写端共用。
 */
export function RankingInput({ question, value, onChange, allowedOptions }: { question: Question; value: any; onChange: (v: any) => void; allowedOptions?: Set<string> | null }) {
  const q = question;
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);
  // 0907 选项关联：只展示允许的选项
  const opts = visibleOptions(q, allowedOptions);
  const arr: string[] = Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
  const selected = arr.filter((v) => opts.some((o) => o.value === v));
  const limit = selectLimitOf(q);
  const atMax = limit?.max != null && selected.length >= limit.max;
  const rows = [
    ...selected.map((v) => ({ value: v, rank: selected.indexOf(v) + 1, image: opts.find((o) => o.value === v)?.image })),
    ...opts.filter((o) => !selected.includes(o.value)).map((o) => ({ value: o.value, rank: 0, image: o.image })),
  ];
  const toggle = (v: string) => {
    if (selected.includes(v)) onChange(selected.filter((x) => x !== v));
    else if (!atMax) onChange([...selected, v]);
  };
  const limitText = limit ? [limit.minHint, limit.maxHint].filter(Boolean).join('，') : '';
  return (
    <div className="ranking-group">
      <div className="ranking-hint">
        点击选项即按点击顺序排位，再点一次取消{limitText ? `（${limitText}）` : ''} · 已选 {selected.length} 项
      </div>
      {rows.map((r) => {
        const locked = !r.rank && atMax;
        return (
          <button
            type="button"
            key={r.value}
            className={`ranking-item${r.rank ? ' is-selected' : ''}${locked ? ' is-locked' : ''}`}
            onClick={() => toggle(r.value)}
            disabled={locked}
            aria-pressed={!!r.rank}
            title={locked ? limit?.maxHint ?? '' : r.rank ? '点击取消选择' : '点击排到第 1 位'}
          >
            <span className="ranking-badge" aria-hidden="true">{r.rank || ''}</span>
            <span className="ranking-label">{r.rank ? `第 ${r.rank} 位：` : ''}{r.value}</span>
            {r.image && (
              <img
                src={r.image}
                alt={r.value}
                className="ranking-img"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setLightbox({ src: r.image!, alt: r.value });
                }}
              />
            )}
          </button>
        );
      })}
      {lightbox && <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />}
    </div>
  );
}
